import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { withRouter } from "../../../routes/withRouter";
import { cleanFilter, getSearchTerm, filterCategories } from '../../../action';
import { searchAutocomplete } from '../../../api';
import './search-autocomplete.css';

const IMAGE_BASE_URL = 'https://d22u7e6casvzfe.cloudfront.net/uploads/products/320x320/';

function SearchAutocomplete(props) {
  const { addClass = "", iconClass = "icon-magnifier", text, btnAClass, cleanFilter, getSearchTerm, searchTerm, filterCategories, isMobile = false } = props;
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const submittedRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchResults = useCallback((query) => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    searchAutocomplete(query)
      .then((data) => {
        if (submittedRef.current) return;
        setResults(data || []);
        setIsOpen(true);
        setIsLoading(false);
      })
      .catch(() => {
        setResults([]);
        setIsLoading(false);
      });
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setActiveIndex(-1);
    submittedRef.current = false;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (!submittedRef.current) fetchResults(value);
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setIsOpen(false);
    setResults([]);
    submittedRef.current = true;
    if (searchInput) {
      cleanFilter();
      getSearchTerm(searchInput);
      navigate(`/szukaj/${searchInput}`, { state: { ts: Date.now() } });
    } else {
      cleanFilter();
      filterCategories("");
      navigate(`/produkty`);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const selected = results[activeIndex];
      setIsOpen(false);
      navigate(`/produkty/${selected.category_slug}/${selected.product_slug}`);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleProductClick = () => {
    setIsOpen(false);
  };

  const wrapperClass = isMobile ? "header-search-wrapper-mobile" : "header-search-wrapper";

  return (
    <div className={`header-search ${isMobile ? 'mobile ' : ''}${addClass}`} ref={wrapperRef}>
      {text && <Link to="#" className="search-toggle" title="Search" onClick={(e) => {
        e.preventDefault();
        if (wrapperRef.current) {
          wrapperRef.current.querySelector(`.${wrapperClass}`)?.classList.toggle("show");
          wrapperRef.current.classList.toggle("show");
        }
      }}>{text}</Link>}
      <form onSubmit={handleSubmit}>
        <div className={wrapperClass}>
          <input
            type="text"
            className="form-control"
            placeholder="Szukaj produktów..."
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => !submittedRef.current && results.length > 0 && setIsOpen(true)}
            autoComplete="off"
          />
          <button className={iconClass + " btn " + btnAClass} title="Szukaj" type="submit"></button>

          {isOpen && results.length > 0 && (
            <div className="autocomplete-dropdown">
              {results.map((product, index) => (
                <Link
                  key={product.product_id}
                  to={`/produkty/${product.category_slug}/${product.product_slug}`}
                  className={`autocomplete-item${index === activeIndex ? ' active' : ''}`}
                  onClick={handleProductClick}
                >
                  <div className="autocomplete-item-image">
                    {product.image ? (
                      <img src={IMAGE_BASE_URL + product.image} alt={product.product_name} />
                    ) : (
                      <div className="autocomplete-item-no-image"></div>
                    )}
                  </div>
                  <div className="autocomplete-item-info">
                    <span className="autocomplete-item-name">{product.product_name}</span>
                    {product.category_name && (
                      <span className="autocomplete-item-category">{product.category_name}</span>
                    )}
                  </div>
                </Link>
              ))}
              <Link
                to={`/szukaj/${searchInput}`}
                className="autocomplete-show-all"
                onClick={() => {
                  setIsOpen(false);
                  cleanFilter();
                  getSearchTerm(searchInput);
                }}
              >
                Zobacz wszystkie wyniki
              </Link>
            </div>
          )}

          {isOpen && isLoading && (
            <div className="autocomplete-dropdown">
              <div className="autocomplete-loading">Szukam...</div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

const mapStateToProps = (state) => ({
  searchTerm: state.search.searchTerm || ''
});

export default withRouter(connect(mapStateToProps, { cleanFilter, getSearchTerm, filterCategories })(SearchAutocomplete));
